// Dialogen rond het wiegje: een bedrag kiezen voor een potje, en het wiegje afrekenen.
import type { GuestGift } from '../shared/api-types.ts';
import { parseEuroToCents } from '../shared/payment.ts';
import { postOrder, RateLimited } from './api.ts';
import { cartTotal, withLine, withoutGift } from './cart.ts';
import { add, centsToInput, closeDialog, formatEuro, h, showDialog, toast } from './dom.ts';
import { cartLineFor, currentCart, droppedMessage, giftById, reloadList, setCart } from './store.ts';

const MIN_CENTS = 100;
const MAX_CENTS = 1_000_000;
const DEFAULT_CENTS = 5000;
const CHIP_CENTS = [2000, 5000, 10000];
const NAME_MAX = 60;
const MESSAGE_MAX = 500;
const PUBLIC_NAME_MAX = 40;

let draft = { name: '', message: '', publicName: '' };

export function picFor(g: GuestGift, cls: 'pic' | 'thumb', alt: string): HTMLElement {
  if (g.imageUrl) return h('img', { class: cls, src: g.imageUrl, alt, loading: 'lazy' });
  const letter = g.title.trim().charAt(0).toUpperCase() || '?';
  return h('span', { class: `${cls} blank`, 'aria-hidden': 'true', text: letter });
}

function amountChips(input: HTMLInputElement, target: number, left: number): HTMLElement {
  const values = CHIP_CENTS.filter((v) => !target || v <= left);
  if (target && left > 0 && !values.includes(left)) values.push(left);
  const chips = h('div', { class: 'chips', role: 'group', 'aria-label': 'Snelle bedragen' });
  const sync = (): void => {
    const current = parseEuroToCents(input.value);
    for (const c of chips.querySelectorAll<HTMLButtonElement>('.chip')) {
      c.setAttribute('aria-pressed', String(Number(c.dataset.v) === current));
    }
  };
  for (const v of values) {
    const label = target && v === left ? `Het restbedrag: ${formatEuro(v)}` : formatEuro(v);
    const chip = h('button', { class: 'chip', type: 'button', onclick: () => { input.value = centsToInput(v); sync(); } }, label);
    chip.dataset.v = String(v);
    chips.append(chip);
  }
  input.addEventListener('input', sync);
  sync();
  return chips;
}

function amountError(amount: number | null, target: number, left: number): string | null {
  if (amount === null || amount < MIN_CENTS) return 'Kies een bedrag van minstens 1 euro.';
  if (amount > MAX_CENTS) return `Kies een bedrag tot ${formatEuro(MAX_CENTS)}.`;
  if (target && amount > left) return `Er is nog maar ${formatEuro(left)} nodig. Kies dat bedrag of minder.`;
  return null;
}

export function openPotAmount(g: GuestGift, backToCart: boolean): void {
  const target = g.priceCents;
  const left = g.remainingCents ?? 0;
  const line = cartLineFor(g.id);
  const amount = line ? line.amountCents : target ? Math.min(DEFAULT_CENTS, left) : DEFAULT_CENTS;
  const input = h('input', { id: 'dAmount', type: 'text', inputmode: 'decimal', autocomplete: 'off', value: centsToInput(amount) });
  const err = h('p', { class: 'err', hidden: true, 'aria-live': 'polite' });
  const after = (): void => {
    if (backToCart) openCart();
    else closeDialog();
  };
  const form = h('form', { class: 'dlg' });
  form.addEventListener('submit', (e) => {
    e.preventDefault();
    const cents = parseEuroToCents(input.value);
    const problem = amountError(cents, target, left);
    if (problem !== null || cents === null) {
      err.textContent = problem;
      err.hidden = false;
      return;
    }
    setCart(withLine(currentCart(), { giftId: g.id, amountCents: cents }), line ? 'Bedrag aangepast.' : `${g.title} ligt in je wiegje.`);
    after();
  });
  const remove = (): void => {
    setCart(withoutGift(currentCart(), g.id), `${g.title} uit je wiegje gehaald.`);
    after();
  };
  add(form,
    g.imageUrl ? h('img', { class: 'dimg', src: g.imageUrl, alt: g.title }) : null,
    h('h2', { text: g.title }),
    h('p', { class: 'muted', text: target ? `Er is nog ${formatEuro(left)} nodig om het doel van ${formatEuro(target)} te halen.` : 'Kies zelf hoeveel je geeft. Elk bedrag helpt.' }),
    h('label', { for: 'dAmount' }, 'Jouw bijdrage in euro', input),
    amountChips(input, target, left),
    err,
    h('div', { class: 'actions' },
      line ? h('button', { class: 'btn ghost', type: 'button', onclick: remove }, 'Haal uit wiegje') : null,
      h('button', { class: 'btn ghost', type: 'button', onclick: after }, backToCart ? 'Terug naar je wiegje' : 'Annuleer'),
      h('button', { class: 'btn', type: 'submit' }, line ? 'Pas aan' : 'Leg in het wiegje')));
  showDialog(form);
  input.focus();
}

function cartLines(): HTMLElement {
  return h('ul', { class: 'cartlines' }, currentCart().map((l) => {
    const g = giftById(l.giftId);
    if (!g) return null;
    const remove = (): void => {
      setCart(withoutGift(currentCart(), g.id));
      if (currentCart().length) openCart();
      else closeDialog();
    };
    return h('li', null,
      picFor(g, 'thumb', ''),
      h('div', { class: 'what' },
        h('span', { class: 'title', text: g.title }),
        h('span', { class: 'note', text: g.kind === 'potje' ? 'Bijdrage aan het potje' : 'Cadeau' })),
      h('span', { class: 'price', text: formatEuro(l.amountCents) }),
      h('div', { class: 'btns' },
        g.kind === 'potje' ? h('button', { class: 'btn ghost small', type: 'button', onclick: () => openPotAmount(g, true) }, 'Pas aan') : null,
        h('button', { class: 'btn ghost small', type: 'button', 'aria-label': `${g.title} uit je wiegje halen`, onclick: remove }, 'Haal eruit')));
  }));
}

type CheckoutFields = { name: string; message: string; publicName: string };

async function checkout(fields: CheckoutFields, err: HTMLElement, submit: HTMLButtonElement): Promise<void> {
  submit.disabled = true;
  try {
    const lines = currentCart();
    const { name, message, publicName } = fields;
    const result = await postOrder({ name, message, lines, ...(publicName ? { publicName } : {}) });
    if (result.ok) {
      draft = { name: '', message: '', publicName: '' };
      setCart([]);
      closeDialog();
      location.hash = `#/betalen/${result.order.token}`;
      return;
    }
    if (result.code === 'unavailable') {
      const titles = result.giftIds.map((id) => giftById(id)?.title ?? 'een cadeau');
      setCart(lines.filter((l) => !result.giftIds.includes(l.giftId)));
      const alsoDropped = await reloadList().catch(() => []);
      openCart([...titles, ...alsoDropped.filter((t) => !titles.includes(t))]);
      return;
    }
    err.textContent = 'De lijst is nog niet helemaal klaar. Probeer het later opnieuw.';
    err.hidden = false;
  } catch (e) {
    if (e instanceof RateLimited) {
      err.textContent = e.message;
      err.hidden = false;
    } else {
      toast('Dat lukte niet. Probeer het opnieuw.');
    }
  }
  submit.disabled = currentCart().length === 0;
}

export function openCart(dropped: string[] = []): void {
  const cart = currentCart();
  const hasCadeau = cart.some((l) => giftById(l.giftId)?.kind === 'cadeau');
  const nameInput = h('input', { id: 'dName', type: 'text', maxlength: NAME_MAX, autocomplete: 'name', value: draft.name, 'aria-describedby': 'dNameHint' });
  const msgInput = h('textarea', { id: 'dMsg', maxlength: MESSAGE_MAX, placeholder: 'Een berichtje voor ons en de kleine (optioneel)' });
  msgInput.value = draft.message;
  const publicInput = h('input', { id: 'dPublic', type: 'text', maxlength: PUBLIC_NAME_MAX, autocomplete: 'off', value: draft.publicName, 'aria-describedby': 'dPublicHint' });
  publicInput.addEventListener('input', () => { draft = { ...draft, publicName: publicInput.value }; });
  nameInput.addEventListener('input', () => { draft = { ...draft, name: nameInput.value }; });
  msgInput.addEventListener('input', () => { draft = { ...draft, message: msgInput.value }; });
  const err = h('p', { class: 'err', 'aria-live': 'polite', hidden: dropped.length === 0, text: dropped.length ? droppedMessage(dropped) : '' });
  const submit = h('button', { class: 'btn', type: 'submit', disabled: cart.length === 0 }, 'Ga verder naar betalen');
  const form = h('form', { class: 'dlg' });
  form.addEventListener('submit', (e) => {
    e.preventDefault();
    const name = nameInput.value.trim();
    if (!name) {
      err.textContent = 'Vul je naam in, zo weten we van wie het komt.';
      err.hidden = false;
      nameInput.focus();
      return;
    }
    if (!currentCart().length) return;
    void checkout({ name, message: msgInput.value.trim(), publicName: hasCadeau ? publicInput.value.trim() : '' }, err, submit);
  });
  add(form,
    h('h2', { text: 'Je wiegje' }),
    err,
    cart.length ? cartLines() : h('p', { class: 'muted', text: 'Je wiegje is nog leeg.' }),
    cart.length ? h('div', { class: 'total' }, h('span', { text: 'Samen' }), h('b', { class: 'price', text: formatEuro(cartTotal(cart)) })) : null,
    h('div', { class: 'fieldgroup' },
      h('label', { for: 'dName' }, 'Je naam', nameInput),
      h('span', { id: 'dNameHint', class: 'hint', text: 'Alleen wij zien je naam en je bericht.' })),
    h('label', { for: 'dMsg' }, 'Bericht', msgInput),
    hasCadeau ? h('div', { class: 'fieldgroup' },
      h('label', { for: 'dPublic' }, 'Naam op de lijst (optioneel)', publicInput),
      h('span', { id: 'dPublicHint', class: 'hint', text: 'Zo zien anderen van wie het cadeau komt. Laat leeg om anoniem te blijven.' })) : null,
    h('p', { class: 'muted small-text', text: 'Je betaalt alles in één overschrijving.' }),
    h('div', { class: 'actions' }, h('button', { class: 'btn ghost', type: 'button', onclick: closeDialog }, 'Verder kiezen'), submit));
  showDialog(form);
}
